import React, { useMemo, useState } from 'react'

import {
    CircularProgress,
    IconButton,
    Typography,
    useTheme,
} from '@material-ui/core'
import { Close, Person } from '@material-ui/icons'
import { Pagination } from '@material-ui/lab'

import { ModelMap, User } from '../../../models'
import { Container } from '../../../components'
import { SearchField } from '../../../components/SearchField'
import { usePagination } from '../../../hooks'
import { UserActivity } from '../../../hooks/useOrganizationManagement'
import { UserActivityTable } from './UserActivityTable'
import { typographyStyle } from '../../../theme/typographyStyle'

interface Props {
    userDetail: User | null
    isLoading: boolean
    userDetailActivityMap: ModelMap<UserActivity>
    onClose: () => void
}

type Order = 'asc' | 'desc'
type SortableColumns = 'name' | 'last-activity' | 'activity-count'

export const UserActivitySummary = (props: Props) => {
    const { userDetail, isLoading, userDetailActivityMap, onClose } = props

    const theme = useTheme()

    const [searchState, setSearchState] = useState<string>('')
    const [order, setOrder] = useState<Order>('asc')
    const [orderBy, setOrderBy] = useState<SortableColumns>('name')

    const sortedWorkspaceUsers = useMemo(() => {
        const filtered =
            userDetail?.workspaces.filter((workspaceUser) =>
                workspaceUser.active_workspace.name
                    .toLowerCase()
                    .includes(searchState.toLowerCase()),
            ) || []

        return filtered.sort((a, b) => {
            if (orderBy === 'name') {
                const aName = a.active_workspace.name || ''
                const bName = b.active_workspace.name || ''
                return order === 'desc'
                    ? bName.localeCompare(aName)
                    : aName.localeCompare(bName)
            }
            if (orderBy === 'last-activity') {
                const aTime =
                    userDetailActivityMap[a.id]?.latest_timestamp || ''
                const bTime =
                    userDetailActivityMap[b.id]?.latest_timestamp || ''
                return order === 'desc'
                    ? bTime.localeCompare(aTime)
                    : aTime.localeCompare(bTime)
            }
            if (orderBy === 'activity-count') {
                const aCount = userDetailActivityMap[a.id]?.log_count || 0
                const bCount = userDetailActivityMap[b.id]?.log_count || 0
                return order === 'desc' ? bCount - aCount : aCount - bCount
            }
            return 0
        })
    }, [userDetail, searchState, order, orderBy, userDetailActivityMap])

    const { page, setPage, pageData, numPages, start, end } = usePagination(
        sortedWorkspaceUsers,
    )

    const handleSort = (property: SortableColumns) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
        setPage(1)
    }

    return (
        <Container
            style={{
                flex: 1,
                flexDirection: 'column',
            }}
        >
            <Container
                style={{
                    padding: theme.spacing(1, 2),
                    borderBottom: `1px solid ${theme.palette.primary.main}`,
                }}
            >
                <Container style={{ flexDirection: 'column' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                        }}
                    >
                        User Activity History
                    </span>
                    <Typography
                        variant="subtitle1"
                        style={{
                            marginTop: 4,
                            display: 'flex',
                            alignItems: 'center',
                            gap: theme.spacing(1),
                        }}
                    >
                        <Person fontSize="small" />
                        {userDetail?.name}
                    </Typography>
                </Container>
                <IconButton
                    onClick={onClose}
                    size="small"
                    style={{ marginLeft: 'auto' }}
                >
                    <Close />
                </IconButton>
            </Container>
            {isLoading || !userDetail ? (
                <Container
                    style={{
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <CircularProgress size={50} />
                </Container>
            ) : (
                <Container
                    style={{
                        flex: 1,
                        flexDirection: 'column',
                    }}
                >
                    <Container style={{ padding: theme.spacing(1, 2) }}>
                        <SearchField
                            style={{ minWidth: 200 }}
                            placeholder="Search properties"
                            value={searchState}
                            onChange={(e) => setSearchState(e.target.value)}
                        />
                    </Container>
                    <UserActivityTable
                        workspaceUsers={pageData}
                        userActivityMap={userDetailActivityMap}
                        order={order}
                        orderBy={orderBy}
                        onSort={handleSort}
                    />
                    <Container
                        style={{
                            ...typographyStyle(theme.typography.subtitle1),
                            borderTop: `1px solid ${theme.palette.grey[400]}`,
                            padding: theme.spacing(1),
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}
                    >
                        <Container
                            style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Pagination
                                count={numPages}
                                page={page}
                                onChange={(_: unknown, newPage: number) => {
                                    setPage(newPage)
                                }}
                            />
                            <span style={{ marginLeft: theme.spacing(2) }}>
                                {start} - {end} of{' '}
                                {sortedWorkspaceUsers?.length ?? 0}
                            </span>
                        </Container>
                    </Container>
                </Container>
            )}
        </Container>
    )
}
